<script setup lang="ts">
import { RouterView, useRouter } from 'vue-router'
import { computed, onBeforeMount, watch, reactive, ref } from 'vue'

import { browserTimeNow } from '@/plugins/utils'
import NavBar from './components/NavBar.vue'
import Sidebar from './components/Sidebar.vue'
import AuthError from './components/AuthError.vue'
import TcModal from '@/components/tcmodal/TcModal.vue'
import { useFirebaseStore } from './stores/firebase'
import type { MainNavItem, MainNavChild } from './types/all'

const router = useRouter()
const firebaseStore = useFirebaseStore()
const browserTime = ref(browserTimeNow())
const isSidebarOpen = ref(false)
const termsAccepted = ref(false)
const forceShowTcModal = ref(false)

// Sync store → local ref when auth finishes loading
watch(
  () => firebaseStore.termsAccepted,
  (newVal) => {
    termsAccepted.value = newVal
    if (newVal) forceShowTcModal.value = false
  },
  { immediate: true },
)

const showTcModal = computed({
  get: () => forceShowTcModal.value || (firebaseStore.isAuthenticated && !termsAccepted.value),
  set: (val: boolean) => {
    forceShowTcModal.value = val
  },
})

// Navigation items for the sidebar
const navigationItems = reactive<MainNavItem[]>([
  {
    title: 'Main',
    items: [
      { text: 'Dashboard', link: '/dashboard', active: true },
      //   { text: 'Chat', link: '/travelers' },
      //  { text: 'Compliance', link: '/insights' },
    ],
  },
  // {
  //   title: 'Management',
  //   items: [
  //     { text: 'Users', link: '/users' },
  //     {
  //       text: 'Settings',
  //       link: '/settings',
  //       expanded: false,
  //       children: [
  //         { text: 'Profile', link: '/settings/profile' },
  //         { text: 'Security', link: '/settings/security' },
  //       ],
  //     },
  //   ],
  // },
  //{
  //  title: 'Resources',
  // items: [{ text: 'Request Feature', link: '/help' }],
  // },
])

// Handle navigation item expansion
const toggleNavItem = (item: MainNavChild) => {
  item.expanded = !item.expanded
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

// Watch for authentication state changes
watch(
  () => firebaseStore.isAuthenticated,
  (isAuthenticated, previousState) => {
    // Only redirect if changing from authenticated to not authenticated
    // This prevents redirect loops and only triggers on actual sign-outs
    if (previousState === true && isAuthenticated === false) {
      // User has been signed out, redirect to login
      router.push('/login')
    }
  },
)
onBeforeMount(() => {
  setInterval(() => {
    browserTime.value = browserTimeNow()
  }, 1000)
})
</script>

<template>
  <AuthError v-if="firebaseStore.hasAuthError" />
  <div v-else class="h-screen flex bg-gray-50">
    <!-- Your Terms & Conditions Modal -->
    <TcModal v-if="showTcModal" v-model:termsAccepted="termsAccepted" />
    <!-- Sidebar Component (shown on authenticated routes) -->
    <Sidebar
      v-if="$route.meta.requiresAuth !== false"
      :navigation-items="navigationItems"
      v-model:isSidebarOpen="isSidebarOpen"
      @toggle-item="toggleNavItem"
    >
      <template #logo>
        <div class="flex items-center space-x-2">
          <svg class="h-8 w-8 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
          </svg>
          <span class="text-gray-900 font-medium">Flywell</span>
        </div>
      </template>
      <template #footer>
        <div class="text-center text-gray-500 text-sm">v0.1.5</div>
        <div class="italic text-xs text-center">{{ browserTime }}</div>
      </template>
    </Sidebar>
    <!-- Main Content Area -->
    <div class="flex-1 flex flex-col" :class="{ 'md:ml-44': $route.meta.requiresAuth !== false }">
      <!-- Top Navigation Bar (shown on authenticated routes) -->
      <NavBar v-if="$route.meta.requiresAuth !== false" @toggle-sidebar="toggleSidebar" />
      <!-- Page Content with Flex Layout -->
      <main class="flex-1 flex flex-col overflow-y-auto">
        <!-- Router content that will grow to fill available space -->
        <div class="flex-1 py-2 px-4 sm:px-6 lg:px-8">
          <RouterView />
        </div>

        <!-- Footer that will stay at the bottom -->
        <footer class="mt-auto pb-4 px-4 sm:px-6 lg:px-8">
          <div class="max-w-full border border-gray-100 mx-auto bg-white p-6 rounded-2xl">
            <div
              class="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-600"
            >
              <div class="mb-4 sm:mb-0">
                <p>&copy; 2025 BWC NY Management INC. All rights reserved.</p>
              </div>
              <div class="flex space-x-6">
                <a
                  class="cursor-pointer text-blue-600 hover:text-blue-800 transition-colors"
                  @click="showTcModal = true"
                >
                  Terms &amp; Conditions
                </a>
                <a
                  class="cursor-pointer text-blue-600 hover:text-blue-800 transition-colors"
                  @click="showTcModal = true"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
            <p class="mt-4 text-xs text-gray-500">
              This application is provided for informational purposes only. Information displayed
              may not be current or accurate. Always verify critical travel information with your
              official documentation or authorized travel agent. Unauthorized use, reproduction, or
              distribution of this application or its contents is strictly prohibited.
            </p>
          </div>
        </footer>
      </main>
    </div>
  </div>
</template>
